import React, { useEffect, useState } from 'react';
import {
    Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Typography, Chip, TablePagination
} from '@mui/material';
import { LeaveBalanceService } from '../../services/leave-balance.service';
import { UserLeaveBalanceSummary } from '../../interfaces/leave.interface';
import { useToast } from '../../context/ToastContext';
import { useTranslation } from 'react-i18next';
import { displayUsername } from '../../utils/userUtils';

const AllUserLeaveBalances: React.FC = () => {
    const [summaries, setSummaries] = useState<UserLeaveBalanceSummary[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const toast = useToast();
    const { t } = useTranslation(['leave', 'common']);

    useEffect(() => {
        fetchBalances();
    }, []);

    const fetchBalances = async () => {
        try {
            const data = await LeaveBalanceService.getAllUserBalances();
            setSummaries(data);
        } catch (err) {
            toast.showApiError(err, t('allUserLeaveBalances.errors.fetch'));
        } finally {
            setLoading(false);
        }
    };

    const leaveTypes = Array.from(new Set(summaries.flatMap(s => s.balances.map(b => b.leaveType))));
    const paginatedSummaries = summaries.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    if (loading) return <Typography>{t('loading')}</Typography>;

    return (
        <Box sx={{ padding: 3 }}>
            <Typography variant="h5" sx={{ marginBottom: 3 }}>{t('allUserLeaveBalances.title')}</Typography>

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell rowSpan={2}>{t('allUserLeaveBalances.user')}</TableCell>
                            {leaveTypes.map((type) => (
                                <TableCell key={type} colSpan={3} align="center">{type}</TableCell>
                            ))}
                        </TableRow>
                        <TableRow>
                            {leaveTypes.map((type) => (
                                <React.Fragment key={type}>
                                    <TableCell align="center">{t('allUserLeaveBalances.allocated')}</TableCell>
                                    <TableCell align="center">{t('allUserLeaveBalances.used')}</TableCell>
                                    <TableCell align="center">{t('allUserLeaveBalances.remaining')}</TableCell>
                                </React.Fragment>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedSummaries.map((summary) => (
                            <TableRow key={summary.userId}>
                                <TableCell>{displayUsername(summary.userName)}</TableCell>
                                {leaveTypes.map((type) => {
                                    const balance = summary.balances.find(b => b.leaveType === type);
                                    if (!balance) {
                                        return (
                                            <React.Fragment key={type}>
                                                <TableCell align="center">0</TableCell>
                                                <TableCell align="center">0</TableCell>
                                                <TableCell align="center">0</TableCell>
                                            </React.Fragment>
                                        );
                                    }
                                    return (
                                        <React.Fragment key={type}>
                                            <TableCell align="center">
                                                {balance.allocated}
                                                {balance.isOverride && (
                                                    <Chip label={t('allUserLeaveBalances.override')} size="small" color="info" sx={{ ml: 0.5, height: 18, fontSize: '0.65rem' }} />
                                                )}
                                            </TableCell>
                                            <TableCell align="center">{balance.availed}</TableCell>
                                            <TableCell align="center" sx={{ color: balance.remaining < 0 ? 'error.main' : 'inherit', fontWeight: balance.remaining < 0 ? 'bold' : 'normal' }}>
                                                {balance.remaining}
                                            </TableCell>
                                        </React.Fragment>
                                    );
                                })}
                            </TableRow>
                        ))}
                        {summaries.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={1 + leaveTypes.length * 3} align="center">
                                    {t('allUserLeaveBalances.empty')}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={summaries.length}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[10, 20, 50, 100]}
            />
        </Box>
    );
};

export default AllUserLeaveBalances;
